/**
 * slideshow-hotspots.js
 *
 * Reusable Web Component for hotspots on cover-fitted images.
 * Calculates hotspot positions based on how object-fit: cover crops the image.
 *
 * Usage:
 * <slideshow-hotspots data-slideshow-id="slideshow-xxx">
 *   <div class="slideshow-hotspot"
 *        data-x-desktop="50" data-y-desktop="30"
 *        data-x-mobile="60" data-y-mobile="40">
 *     ...
 *   </div>
 * </slideshow-hotspots>
 */

/**
 * Calculates how object-fit: cover renders an image within a container
 * @param {number} containerWidth - Width of the container
 * @param {number} containerHeight - Height of the container
 * @param {number} imageWidth - Natural width of the image
 * @param {number} imageHeight - Natural height of the image
 * @returns {{ renderedWidth: number, renderedHeight: number, offsetX: number, offsetY: number }}
 */
function getCoverFitDimensions(containerWidth, containerHeight, imageWidth, imageHeight) {
  const containerRatio = containerWidth / containerHeight;
  const imageRatio = imageWidth / imageHeight;

  if (imageRatio > containerRatio) {
    const renderedHeight = containerHeight;
    const renderedWidth = containerHeight * imageRatio;
    return {
      renderedWidth,
      renderedHeight,
      offsetX: (renderedWidth - containerWidth) / 2,
      offsetY: 0
    };
  }

  const renderedWidth = containerWidth;
  const renderedHeight = containerWidth / imageRatio;
  return {
    renderedWidth,
    renderedHeight,
    offsetX: 0,
    offsetY: (renderedHeight - containerHeight) / 2
  };
}

/**
 * Maps a percentage position on the original image to container pixel coordinates
 * @param {number} xPercent - X position as percentage of original image (0-100)
 * @param {number} yPercent - Y position as percentage of original image (0-100)
 * @param {{ renderedWidth: number, renderedHeight: number, offsetX: number, offsetY: number }} coverFit
 * @returns {{ x: number, y: number }}
 */
function mapToContainerPosition(xPercent, yPercent, coverFit) {
  return {
    x: (xPercent * coverFit.renderedWidth) / 100 - coverFit.offsetX,
    y: (yPercent * coverFit.renderedHeight) / 100 - coverFit.offsetY
  };
}

/**
 * Checks if a position is within the visible container bounds
 * @param {number} x - X position in pixels
 * @param {number} y - Y position in pixels
 * @param {number} containerWidth - Container width
 * @param {number} containerHeight - Container height
 * @returns {boolean}
 */
function isWithinBounds(x, y, containerWidth, containerHeight) {
  return x >= 0 && x <= containerWidth && y >= 0 && y <= containerHeight;
}

/**
 * Determines the alignment class needed to prevent card overflow
 * @param {number} posX - Hotspot X position in pixels
 * @param {number} containerWidth - Container width
 * @param {boolean} isMobile - Whether we're on mobile viewport
 * @returns {string|null} - Alignment class name or null if centered
 */
function getCardAlignment(posX, containerWidth, isMobile) {
  const cardWidth = 200;
  const edgePadding = 16;

  if (isMobile) {
    const cardHalfWidth = cardWidth / 2;
    if (posX - cardHalfWidth < edgePadding) {
      return "slideshow-hotspot--align-left";
    }
    if (posX + cardHalfWidth > containerWidth - edgePadding) {
      return "slideshow-hotspot--align-right";
    }
    return null;
  }

  const desktopCardOffset = -14;
  if (posX + desktopCardOffset + cardWidth > containerWidth - edgePadding) {
    return "slideshow-hotspot--align-right";
  }
  return null;
}

if (!customElements.get("slideshow-hotspots")) {
  customElements.define(
    "slideshow-hotspots",
    class SlideshowHotspots extends HTMLElement {
      constructor() {
        super();
        this.hotspots = [];
        this.image = null;
        this.container = null;
        this.resizeObserver = null;
        this.mobileBreakpoint = 750;
        this.handleSlideChange = this.handleSlideChange.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
      }

      connectedCallback() {
        this.hotspots = [...this.querySelectorAll(".slideshow-hotspot")];
        if (!this.hotspots.length) return;

        this.container = this.closest(".slideshow-slide");
        this.image = this.container?.querySelector(".slideshow-media img");

        if (!this.image || !this.container) return;

        if (this.image.complete) {
          this.calculatePositions();
        } else {
          this.image.addEventListener("load", () => this.calculatePositions(), { once: true });
        }

        this.resizeObserver = new ResizeObserver(
          debounce(() => this.calculatePositions(), 100)
        );
        this.resizeObserver.observe(this.container);

        this.initClickHandlers();

        document.addEventListener("slideshow:slide-change", this.handleSlideChange);
      }

      disconnectedCallback() {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
        document.removeEventListener("slideshow:slide-change", this.handleSlideChange);
        document.removeEventListener("click", this.handleDocumentClick);
        document.removeEventListener("keydown", this.handleKeydown);
      }

      /**
       * Calculates and applies positions for all hotspots.
       * Coordinates are percentages of the original image, mapped to the visible
       * container area accounting for object-fit: cover cropping.
       */
      calculatePositions() {
        if (!this.image || !this.container) return;

        const containerWidth = this.container.offsetWidth;
        const containerHeight = this.container.offsetHeight;
        const imageWidth = this.image.naturalWidth;
        const imageHeight = this.image.naturalHeight;

        if (!containerWidth || !containerHeight || !imageWidth || !imageHeight) return;

        const coverFit = getCoverFitDimensions(containerWidth, containerHeight, imageWidth, imageHeight);
        const isMobile = window.innerWidth < this.mobileBreakpoint;

        this.hotspots.forEach((hotspot) => {
          this.positionHotspot(hotspot, coverFit, containerWidth, containerHeight, isMobile);
        });
      }

      /**
       * Positions a single hotspot element
       * @param {HTMLElement} hotspot - The hotspot element
       * @param {{ renderedWidth: number, renderedHeight: number, offsetX: number, offsetY: number }} coverFit
       * @param {number} containerWidth - Container width
       * @param {number} containerHeight - Container height
       * @param {boolean} isMobile - Whether we're on mobile viewport
       */
      positionHotspot(hotspot, coverFit, containerWidth, containerHeight, isMobile) {
        const xPercent = parseFloat(hotspot.dataset[isMobile ? "xMobile" : "xDesktop"]) || 0;
        const yPercent = parseFloat(hotspot.dataset[isMobile ? "yMobile" : "yDesktop"]) || 0;

        const pos = mapToContainerPosition(xPercent, yPercent, coverFit);

        hotspot.style.left = `${(pos.x / containerWidth) * 100}%`;
        hotspot.style.top = `${(pos.y / containerHeight) * 100}%`;

        hotspot.classList.remove("slideshow-hotspot--align-left", "slideshow-hotspot--align-right");
        const alignment = getCardAlignment(pos.x, containerWidth, isMobile);
        if (alignment) {
          hotspot.classList.add(alignment);
        }
      }

      /**
       * Initializes click handlers for mobile tap-to-reveal behavior
       */
      initClickHandlers() {
        this.hotspots.forEach((hotspot) => {
          const toggle = hotspot.querySelector(".slideshow-hotspot__toggle");
          if (toggle) {
            toggle.addEventListener("click", (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.toggleHotspot(hotspot);
            });
          }
        });

        document.addEventListener("click", this.handleDocumentClick);
        document.addEventListener("keydown", this.handleKeydown);
      }

      /**
       * Toggles a hotspot's active state
       * @param {HTMLElement} hotspot - The hotspot element to toggle
       */
      toggleHotspot(hotspot) {
        const isActive = hotspot.classList.contains("is-active");
        this.closeAllHotspots();
        if (!isActive) {
          hotspot.classList.add("is-active");
        }
      }

      /**
       * Closes all open hotspots
       */
      closeAllHotspots() {
        this.hotspots.forEach((h) => h.classList.remove("is-active"));
      }

      /**
       * Handles document click to close hotspots when clicking outside
       * @param {Event} event - The click event
       */
      handleDocumentClick(event) {
        if (!event.target.closest(".slideshow-hotspot")) {
          this.closeAllHotspots();
        }
      }

      /**
       * Handles keydown to close hotspots on Escape
       * @param {KeyboardEvent} event - The keyboard event
       */
      handleKeydown(event) {
        if (event.key === "Escape") {
          this.closeAllHotspots();
        }
      }

      /**
       * Handles slide change event to close hotspots
       * @param {CustomEvent} event - The slide change event
       */
      handleSlideChange(event) {
        this.closeAllHotspots();
      }
    }
  );
}
